/* Progressive presentation layer. Recharge remains responsible for selling-plan
 * selection and cart data; native Shopify quantity and variant handlers stay intact. */
if (!customElements.get('twice-purchase-polish')) {
  customElements.define('twice-purchase-polish', class extends HTMLElement {
    connectedCallback() {
      this.main = this.closest('[id^="MainProduct-"]');
      if (!this.main) return;
      this.variants = JSON.parse(this.querySelector('[data-tw-variants]')?.textContent || '{}');
      this.presets = [1, 2, 3, 4];
      if (this.dataset.singleTube) {
        const url = new URL(location.href);
        if (this.dataset.initialVariant !== this.dataset.singleTube) {
          // Legacy pack links enter the single-tube flow with the same tube count.
          const old = this.variants[this.dataset.initialVariant];
          const requested = Number(url.searchParams.get('quantity')) || 1;
          url.searchParams.set('variant', this.dataset.singleTube);
          url.searchParams.set('quantity', String(Math.max(1, requested) * (old?.count || 1)));
          const plan = url.searchParams.get('selling_plan');
          if (plan && !(plan in this.variants[this.dataset.singleTube].plans)) url.searchParams.delete('selling_plan');
          location.replace(url.href);
          return;
        }
        this.main.setAttribute('data-tw-single-tube', '');
      }
      this.schedule = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.connectWidget();
          this.connectQuantity();
          this.syncPrice();
        });
      };
      this.mainObserver = new MutationObserver(this.schedule);
      this.mainObserver.observe(this.main, { childList: true, subtree: true });
      this.main.addEventListener('change', this.schedule);
      this.schedule();
      customElements.whenDefined('recharge-subscription-widget').then(() => {
        if (this.isConnected) this.schedule();
      });
    }

    connectWidget() {
      const widget = this.main.querySelector('recharge-subscription-widget');
      const root = widget?.shadowRoot;
      if (!root) {
        if (this.hasAttribute('data-quantity-pilot') && this.widgetRoot) {
          this.widgetObserver?.disconnect();
          this.widgetRoot.removeEventListener('change', this.schedule);
          this.widgetRoot = null;
        }
        return;
      }
      if (this.widgetRoot !== root) {
        this.widgetObserver?.disconnect();
        this.widgetRoot?.removeEventListener('change', this.schedule);
        this.widgetRoot = root;
        this.widgetObserver = new MutationObserver(this.schedule);
        this.widgetObserver.observe(root, {
          childList: true, subtree: true, characterData: true,
          attributes: true, attributeFilter: ['rc-selected', 'checked', 'part']
        });
        root.addEventListener('change', this.schedule);
      }
      // Focus treatment follows the actual native input, including keyboard use.
      if (!root.querySelector('style[data-tw-focus]')) {
        const style = document.createElement('style');
        style.dataset.twFocus = '';
        style.textContent = '[part~="rc-purchase-option__label"]:has(input:focus-visible){outline:2px solid #111;outline-offset:4px}';
        root.append(style);
      }
      const benefits = root.querySelector('rc-benefits')?.shadowRoot;
      if (benefits && !benefits.querySelector('style[data-tw-benefits]')) {
        const style = document.createElement('style');
        style.dataset.twBenefits = '';
        style.textContent = 'ul{display:flex!important;flex-wrap:wrap;gap:4px 18px!important;margin:0!important;padding:0!important}li{margin:0!important}p{margin:0!important;line-height:1.4!important}';
        benefits.append(style);
      }
      const plans = root.querySelector('rc-selling-plans')?.shadowRoot;
      if (plans && !plans.querySelector('style[data-tw-plans]')) {
        const style = document.createElement('style');
        style.dataset.twPlans = '';
        style.textContent = 'label:has(input:focus-visible){outline:2px solid #111;outline-offset:3px}';
        plans.append(style);
      }
      if (this.hasAttribute('data-onetime-only')) {
        const onetime = root.querySelector('input[name="purchaseOption"][value="onetime"]');
        if (onetime && !onetime.disabled) {
          if (!onetime.checked) onetime.click();
          if (onetime.checked && !widget.hasAttribute('data-tw-onetime-only')) widget.setAttribute('data-tw-onetime-only', '');
        }
      }
      if (this.dataset.onetimeFirst === 'true') {
        if (!widget.hasAttribute('data-tw-onetime-first')) widget.setAttribute('data-tw-onetime-first', '');
        const onetime = root.querySelector('input[name="purchaseOption"][value="onetime"]');
        if (!this.defaultApplied && onetime) {
          this.defaultApplied = true;
          // Respect an explicit subscription deep link. Never reset a buyer's choice.
          if (!new URLSearchParams(location.search).has('selling_plan') && !onetime.checked && !onetime.disabled) onetime.click();
        }
      }
    }

    selectedVariant() {
      const id = this.main.querySelector('form.main-atc-form input[name="id"]')?.value;
      return this.variants[id];
    }

    quantityLabel(qty, model = this.selectedVariant()) {
      return qty === 1 ? model.one : model.many.replace('__COUNT__', qty);
    }

    connectQuantity() {
      if (!this.hasAttribute('data-quantity-pilot')) return;
      const form = this.main.querySelector('form.main-atc-form');
      const input = form?.querySelector('input[name="quantity"]');
      const widget = this.main.querySelector('recharge-subscription-widget');
      const control = input?.closest('quantity-input');
      const model = this.selectedVariant();
      if (this.dataset.singleTube) {
        const picker = this.main.querySelector('variant-selects');
        if (picker) { picker.hidden = true; picker.style.setProperty('display', 'none', 'important'); }
      }
      const anchor = widget || form?.closest('product-form');
      if (!input || !control || !model || !anchor) {
        // A slow or removed app must never leave the native quantity inaccessible.
        this.quantityHost?.removeAttribute('data-tw-unit-hidden');
        this.quantityHost?.removeAttribute('data-tw-unit-native');
        this.quantityChoice?.remove();
        widget?.removeAttribute('data-tw-quantity-selector');
        return;
      }

      if (this.dataset.singleTube && !this.initialQuantityApplied) {
        this.initialQuantityApplied = true;
        const requested = Number(new URL(location.href).searchParams.get('quantity'));
        if (Number.isSafeInteger(requested) && requested > 0) {
          const previous = input.value;
          input.value = String(requested);
          if (!input.validity.valid) input.value = previous;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      if (this.quantityInput !== input) {
        this.quantityInput?.removeEventListener('input', this.schedule);
        this.quantityObserver?.disconnect();
        this.quantityInput = input;
        input.addEventListener('input', this.schedule);
        this.quantityObserver = new MutationObserver(this.schedule);
        this.quantityObserver.observe(input, {
          attributes: true, attributeFilter: ['min', 'max', 'step', 'disabled', 'value']
        });
      }
      // Hide the wrapper as well as the stepper so it leaves no empty CTA column.
      // The scoped selector also wins against the theme's display:grid!important.
      const parent = control.parentElement;
      const host = parent?.classList.contains('pb-1') && parent.children.length === 1 ? parent : control;
      if (this.quantityHost !== host) {
        this.quantityHost?.removeAttribute('data-tw-unit-hidden');
        this.quantityHost?.removeAttribute('data-tw-unit-native');
        this.quantityHost = host;
      }
      if (!host.hasAttribute('data-tw-unit-native')) host.setAttribute('data-tw-unit-native', '');
      control.classList.remove('tw-unit-native-hidden');
      widget?.setAttribute('data-tw-quantity-selector', '');

      if (!this.quantityChoice?.isConnected) {
        const wrapper = document.createElement('div');
        wrapper.className = 'tw-unit-choice';
        const style = document.createElement('style');
        style.textContent = `
          [id^="MainProduct-"] .tw-unit-choice{margin:0 0 12px;font:400 14px/1.3 "Caslon Ionic",serif;color:#111}
          [id^="MainProduct-"] .tw-unit-choice *{box-sizing:border-box}
          [id^="MainProduct-"] .tw-unit-choice fieldset{min-width:0;border:0;padding:0;margin:0}
          [id^="MainProduct-"] .tw-unit-choice legend{padding:0;margin:0 0 8px;font:inherit}
          [id^="MainProduct-"] .tw-unit-choice__buttons{display:flex;gap:8px;width:100%}
          [id^="MainProduct-"] .tw-unit-choice button{appearance:none;flex:1;min-width:0;min-height:44px;padding:9px 4px;border:1px solid #d7d3c9;border-radius:0;background:#fff;color:#111;font:inherit;line-height:1.2;text-transform:none;letter-spacing:normal;box-shadow:none;cursor:pointer}
          [id^="MainProduct-"] .tw-unit-choice button[data-quantity="0"]{flex:.75;min-width:44px}
          [id^="MainProduct-"] .tw-unit-choice button[aria-pressed="true"]{background:#ffdc00;border-color:#111;box-shadow:inset 0 0 0 1px #111}
          [id^="MainProduct-"] .tw-unit-choice button:focus-visible{outline:2px solid #111;outline-offset:3px}
          [id^="MainProduct-"] .tw-unit-choice button:disabled{opacity:.4;cursor:not-allowed}
          [id^="MainProduct-"] form.main-atc-form [data-tw-unit-native][data-tw-unit-hidden]{display:none!important}
          [id^="MainProduct-"] recharge-subscription-widget[data-tw-quantity-selector]::part(rc-content-wrap){gap:8px}
          [id^="MainProduct-"] recharge-subscription-widget[data-tw-quantity-selector]::part(rc-purchase-option){padding:10px 12px}
          [id^="MainProduct-"] recharge-subscription-widget[data-tw-quantity-selector]::part(rc-purchase-option__label){min-height:44px;gap:8px}
          [id^="MainProduct-"] recharge-subscription-widget[data-tw-quantity-selector]::part(rc-purchase-option__sub-container){padding-top:6px}
          [id^="MainProduct-"] recharge-subscription-widget[data-tw-quantity-selector]::part(rc-purchase-option__prices){gap:5px;font-size:16px}
          [id^="MainProduct-"] recharge-subscription-widget[data-tw-quantity-selector]::part(tw-unit-price-caption){font-size:12px;font-weight:400;white-space:nowrap;color:#565249}
        `;
        wrapper.append(style);
        const fieldset = document.createElement('fieldset');
        const legend = document.createElement('legend');
        legend.textContent = this.dataset.quantityLabel;
        const buttons = document.createElement('div');
        buttons.className = 'tw-unit-choice__buttons';
        [...this.presets, 0].forEach(n => {
          const button = document.createElement('button');
          button.type = 'button';
          button.dataset.quantity = String(n);
          button.textContent = n === 0 ? this.dataset.more : this.quantityLabel(n, model);
          button.addEventListener('click', () => {
            // Resolve again after a theme/app re-render; do not retain an old form.
            const current = this.main.querySelector('form.main-atc-form input[name="quantity"]');
            if (!current || current.disabled || button.disabled) return;
            this.moreQuantity = n === 0;
            if (n) {
              current.value = String(n);
              current.dispatchEvent(new Event('input', { bubbles: true }));
              current.dispatchEvent(new Event('change', { bubbles: true }));
            }
            this.connectQuantity();
            this.schedule();
            if (!n) current.focus();
          });
          buttons.append(button);
        });
        fieldset.append(legend, buttons);
        wrapper.append(fieldset);
        anchor.before(wrapper);
        this.quantityChoice = wrapper;
      } else if (this.quantityChoice.nextElementSibling !== anchor) {
        anchor.before(this.quantityChoice);
      }
      const qty = Number(input.value);
      const min = Number(input.min) || 1;
      const max = input.max === '' ? Infinity : Number(input.max);
      const step = Number(input.step) || 1;
      const preset = this.presets.includes(qty) && input.value !== '' && input.validity.valid;
      const expanded = Boolean(this.moreQuantity || !preset);
      this.quantityChoice.querySelectorAll('button').forEach(button => {
        const n = Number(button.dataset.quantity);
        const text = n === 0 ? this.dataset.more : this.quantityLabel(n, model);
        if (button.textContent !== text) button.textContent = text;
        const pressed = String(n ? !expanded && n === qty : expanded);
        if (button.getAttribute('aria-pressed') !== pressed) button.setAttribute('aria-pressed', pressed);
        const disabled = input.disabled || (n > 0 && (n < min || n > max || (n - min) % step !== 0));
        if (button.disabled !== disabled) button.disabled = disabled;
      });
      host.toggleAttribute('data-tw-unit-hidden', !expanded);
      // Recharge still owns every price. Clarify its per-bottle prices without
      // replacing app nodes, inputs, selling plans, or add-to-cart handlers.
      this.widgetRoot?.querySelectorAll('[part~="rc-purchase-option__prices"]').forEach(prices => {
        const caption = prices.querySelector('[data-tw-unit-caption]') || document.createElement('span');
        caption.dataset.twUnitCaption = '';
        caption.setAttribute('part', 'tw-unit-price-caption');
        const text = ` / ${model.one}`;
        if (caption.textContent !== text) caption.textContent = text;
        if (!caption.isConnected) prices.append(caption);
      });
    }

    syncPrice() {
      const checked = this.widgetRoot?.querySelector('input[name="purchaseOption"]:checked');
      const option = checked?.closest('[part~="rc-purchase-option"]');
      const price = option?.querySelector('[part~="rc-purchase-option__discounted-price"], [part~="rc-purchase-option__price"]');
      const target = this.main.querySelector('[id^="price-"]');
      const model = this.selectedVariant();
      if (!target || (!price && !model)) {
        if (target?.hasAttribute('data-tw-selected-price')) {
          target.removeAttribute('data-tw-selected-price');
          target.querySelector('.tw-selected-price')?.remove();
        }
        if (this.hasAttribute('data-quantity-pilot') && this.nativeStickyText !== undefined) {
          const sticky = this.main.querySelector('.sticky-cta__price');
          if (sticky && sticky.textContent !== this.nativeStickyText) sticky.textContent = this.nativeStickyText;
        }
        return;
      }
      // Read the app's displayed price, excluding screen-reader-only prefixes.
      const copy = price?.cloneNode(true);
      copy?.querySelectorAll('.rc-visually-hidden, .visually-hidden, [aria-hidden="true"]').forEach(node => node.remove());
      let amount = copy?.textContent.trim() || '';
      if (!amount && !model) return;
      const form = this.main.querySelector('form[id^="product-form-"]:not([id^="product-form-installment-"])');
      const variant = form?.querySelector('input[name="id"]')?.value;
      const plan = form?.querySelector('input[name="selling_plan"]')?.value;
      const notes = this.querySelector('template[data-tw-renewals]')?.content;
      const renewal = checked?.value === 'subscription' && variant && plan ? notes?.querySelector(`[data-variant="${variant}"][data-plan="${plan}"]`) : null;
      let renewalDisplay = this.main.querySelector('.tw-pdp-renewal');
      if (renewal && !renewalDisplay) {
        renewalDisplay = document.createElement('p');
        renewalDisplay.className = 'tw-pdp-renewal';
        renewalDisplay.setAttribute('role', 'status');
        this.main.querySelector('recharge-subscription-widget')?.after(renewalDisplay);
      }
      if (renewalDisplay) {
        renewalDisplay.hidden = !renewal || (renewal.dataset.priceLabel === this.dataset.subscriptionLabel && Boolean(this.widgetRoot?.querySelector('rc-benefits')));
        if (renewal && renewalDisplay.textContent !== renewal.textContent.trim()) renewalDisplay.textContent = renewal.textContent.trim();
      }
      const benefits = this.widgetRoot?.querySelector('rc-benefits')?.shadowRoot;
      benefits?.querySelectorAll('p').forEach(paragraph => {
        const original = paragraph.dataset.twSavings || paragraph.textContent.trim();
        if (!/^Save\s+[\d.,]+%$/i.test(original)) return;
        paragraph.dataset.twSavings = original;
        const next = original + (renewal ? ' ' + renewal.dataset.savingsNote : '');
        if (paragraph.textContent !== next) paragraph.textContent = next;
      });
      let label = checked?.value === 'subscription' ? (renewal?.dataset.priceLabel || this.dataset.subscriptionLabel) : this.dataset.onetimeLabel;
      if (this.hasAttribute('data-quantity-pilot')) {
        const input = form?.querySelector('input[name="quantity"]');
        const qty = Number(input?.value);
        // Keep the last valid total while the buyer edits an empty/invalid field.
        if (!input || !input.value || !input.validity.valid || !Number.isSafeInteger(qty) || qty < 1) return;
        const cents = plan ? model?.plans[plan] : model?.price;
        if (Number.isFinite(cents)) {
          amount = new Intl.NumberFormat(document.documentElement.lang || 'en', {style:'currency',currency:this.dataset.currency}).format(cents * qty / 100);
          const contents = model.count > 1 ? ` (${model.contents.replace('__COUNT__', model.count * qty)})` : '';
          const units = this.quantityLabel(qty, model) + contents;
          label = `${this.dataset.totalLabel} · ${units} · ${label}`;
        }
      }
      let display = target.querySelector('.tw-selected-price');
      if (!display) {
        display = document.createElement('div');
        display.className = 'tw-selected-price';
        if (this.hasAttribute('data-quantity-pilot')) {
          display.setAttribute('role', 'status');
          display.setAttribute('aria-atomic', 'true');
        }
        const value = document.createElement('span');
        value.className = 'tw-selected-price__amount';
        const caption = document.createElement('span');
        caption.className = 'tw-selected-price__label';
        display.append(value, caption);
        target.append(display);
      }
      if (!target.hasAttribute('data-tw-selected-price')) target.setAttribute('data-tw-selected-price', '');
      const value = display.querySelector('.tw-selected-price__amount');
      const caption = display.querySelector('.tw-selected-price__label');
      if (value.textContent !== amount) value.textContent = amount;
      if (caption.textContent !== label) caption.textContent = label;
      const sticky = this.main.querySelector('.sticky-cta__price');
      if (this.hasAttribute('data-quantity-pilot') && sticky && this.nativeStickyText === undefined) this.nativeStickyText = sticky.textContent;
      const stickyText = `${amount} · ${label}`;
      if (sticky && sticky.textContent !== stickyText) sticky.textContent = stickyText;
    }

    disconnectedCallback() {
      this.mainObserver?.disconnect();
      this.widgetObserver?.disconnect();
      this.main?.removeEventListener('change', this.schedule);
      this.widgetRoot?.removeEventListener('change', this.schedule);
      this.quantityInput?.removeEventListener('input', this.schedule);
      this.quantityObserver?.disconnect();
      this.quantityHost?.removeAttribute('data-tw-unit-hidden');
      this.quantityHost?.removeAttribute('data-tw-unit-native');
      this.quantityChoice?.remove();
      this.main?.querySelector('recharge-subscription-widget')?.removeAttribute('data-tw-quantity-selector');
      this.widgetRoot?.querySelectorAll('[data-tw-unit-caption]').forEach(caption => caption.remove());
      if (this.frame) cancelAnimationFrame(this.frame);
    }
  });
}



